import { createChannelSchema, type ChannelMode } from '@nexo/types';
import { Avatar, Button, Field, Input, Modal, Textarea, cn } from '@nexo/ui';
import { Hash, Volume2 } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type SubmitEvent } from 'react';
import { useNavigate } from 'react-router';
import { useTeam } from '../../layouts/TeamContext';
import { api } from '../../lib/api';
import { cache } from '../../lib/cache';
import { errorMessage, errorsFromApi, useForm, validate } from '../../lib/forms';
import { useMe } from '../auth/AuthProvider';
import { useTranslation } from 'react-i18next';

export function CreateChannelDialog({
  open,
  onClose,
  initialMode = 'text',
}: {
  open: boolean;
  onClose: () => void;
  initialMode?: ChannelMode;
}) {
  const { team, members } = useTeam();
  const me = useMe();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const form = useForm({ name: '', description: '', isPrivate: false, memberIds: [] as string[] });
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<ChannelMode>(initialMode);
  const { t } = useTranslation();
  // Opening from «Canales de voz» preselects voice.
  const [openedWith, setOpenedWith] = useState<{ open: boolean; mode: ChannelMode }>({
    open,
    mode: initialMode,
  });
  if (openedWith.open !== open || openedWith.mode !== initialMode) {
    setOpenedWith({ open, mode: initialMode });
    if (open) setMode(initialMode);
  }

  const mutation = useMutation({
    mutationFn: (input: typeof form.values) => api.channels.create(team.id, { ...input, mode }),
    onSuccess: (channel) => {
      cache.upsertChannel(queryClient, channel);
      close();
      void navigate(`/app/t/${team.id}/c/${channel.id}`);
    },
    onError: (err) => {
      const fieldErrors = errorsFromApi(err);
      if (fieldErrors) form.setErrors(fieldErrors);
      else setError(errorMessage(err));
    },
  });

  function close() {
    form.setValues({ name: '', description: '', isPrivate: false, memberIds: [] });
    form.setErrors({});
    setError(null);
    onClose();
  }

  function onSubmit(event: SubmitEvent) {
    event.preventDefault();
    const { errors } = validate(createChannelSchema, form.values);
    if (errors) return form.setErrors(errors);
    mutation.mutate(form.values);
  }

  const toggleMember = (id: string) =>
    form.set(
      'memberIds',
      form.values.memberIds.includes(id)
        ? form.values.memberIds.filter((m) => m !== id)
        : [...form.values.memberIds, id],
    );

  return (
    <Modal
      open={open}
      onClose={close}
      title={t('Crear canal')}
      description={t('Los canales organizan las conversaciones por tema.')}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            {t('Cancelar')}
          </Button>
          <Button type="submit" form="create-channel" loading={mutation.isPending}>
            {t('Crear canal')}
          </Button>
        </>
      }
    >
      <form id="create-channel" onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <div role="radiogroup" aria-label={t('Tipo de canal')} className="grid grid-cols-2 gap-2">
          {(
            [
              {
                value: 'text',
                icon: <Hash />,
                label: 'Texto',
                hint: 'Mensajes, archivos y tareas',
              },
              {
                value: 'voice',
                icon: <Volume2 />,
                label: 'Voz',
                hint: 'Sala de voz y vídeo siempre abierta',
              },
            ] as const
          ).map((option) => (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={mode === option.value}
              onClick={() => setMode(option.value)}
              className={cn(
                'flex items-start gap-2.5 rounded-xl border p-3 text-left [&_svg]:mt-0.5 [&_svg]:size-4 [&_svg]:shrink-0',
                mode === option.value
                  ? 'border-accent bg-accent-soft text-accent'
                  : 'border-border hover:bg-surface-2',
              )}
            >
              {option.icon}
              <span>
                <span className="block text-sm font-medium">{t(option.label)}</span>
                <span className="block text-xs text-muted">{t(option.hint)}</span>
              </span>
            </button>
          ))}
        </div>
        <Field
          label={t('Nombre')}
          error={form.errors.name}
          hint={
            mode === 'voice'
              ? t('Minúsculas, sin espacios. p. ej. reuniones')
              : t('Minúsculas, sin espacios. p. ej. marketing')
          }
        >
          {(props) => (
            <Input
              {...props}
              autoFocus
              value={form.values.name}
              onChange={(e) => form.set('name', e.target.value.toLowerCase().replace(/\s+/g, '-'))}
            />
          )}
        </Field>
        <Field label={t('Descripción (opcional)')} error={form.errors.description}>
          {(props) => (
            <Textarea
              {...props}
              rows={2}
              value={form.values.description}
              onChange={(e) => form.set('description', e.target.value)}
            />
          )}
        </Field>
        <label className="flex items-start gap-3 rounded-lg border border-border p-3">
          <input
            type="checkbox"
            className="mt-0.5 size-4 accent-[var(--color-accent)]"
            checked={form.values.isPrivate}
            onChange={(e) => form.set('isPrivate', e.target.checked)}
          />
          <span>
            <span className="block text-sm font-medium">{t('Canal privado')}</span>
            <span className="block text-xs text-muted">
              {t('Solo lo verán las personas que añadas.')}
            </span>
          </span>
        </label>
        {form.values.isPrivate && (
          <fieldset>
            <legend className="mb-2 text-sm font-medium">{t('Miembros')}</legend>
            <div className="flex max-h-48 flex-col overflow-y-auto rounded-lg border border-border">
              {members
                .filter((m) => m.id !== me.id)
                .map((member) => (
                  <label
                    key={member.id}
                    className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-surface-2"
                  >
                    <input
                      type="checkbox"
                      className="size-4 accent-[var(--color-accent)]"
                      checked={form.values.memberIds.includes(member.id)}
                      onChange={() => toggleMember(member.id)}
                    />
                    <Avatar
                      id={member.id}
                      name={member.displayName}
                      src={member.avatarUrl}
                      size="sm"
                    />
                    <span className="text-sm">{member.displayName}</span>
                  </label>
                ))}
            </div>
          </fieldset>
        )}
        {error && <p className="text-sm text-danger">{error}</p>}
      </form>
    </Modal>
  );
}
